import {
  ArrowUpRight,
  CaretRight,
  Database,
  Sparkle,
  Star,
  X,
} from "@phosphor-icons/react"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { SearchableList } from "@tokenterminal/core/ui/samsCombobox/searchableList/SearchableList"
import { iconsBySlug } from "modules/market-sectors/constants"
import DashboardImage from "modules/studio/components/DashboardImage"
import { type Command } from "./types"

/**
 * Creates a SearchableList.Item for a given command, wiring up any callbacks or
 * event handlers.
 */
export const makeSearchResultItem = ({
  command,
  handleClose,
  addRecentSearchCommand,
  removeRecentSearchCommand,
  isRecent,
}: {
  command: Command
  handleClose: () => void
  addRecentSearchCommand: (
    commandId: string,
    type?: "ask",
    query?: string
  ) => void
  removeRecentSearchCommand: (id: string) => void
  isRecent?: boolean
}) => {
  let children, typeText, linkProps

  if (!command) {
    console.warn(
      "Tried to render a SearchResultItem with an undefined command",
      command
    )
    return null
  }

  switch (command.type) {
    case "project":
      children = (
        <div className="flex items-center gap-2">
          <Avatar
            src={`https://static1.tokenterminal.com/${command.data.logo}`}
            className="w-4 h-4 rounded-full overflow-hidden"
          />
          {command.data.name}
          {command.data.symbol && (
            <span
              className="text-fg-secondary opacity-80"
              style={{
                fontFamily: "var(--font-geist-mono)",
                fontWeight: 400,
              }}
            >
              {command.data.symbol}
            </span>
          )}
        </div>
      )
      typeText = "Project"
      linkProps = { href: `/projects/${command.data.slug}` }
      break

    case "product":
      children = (
        <div className="flex items-center gap-2">
          {command.data.project ? (
            <>
              {/* <Avatar
                src={`https://static1.tokenterminal.com/${command.data.project?.logo}`}
                className="w-4 h-4 rounded-full overflow-hidden"
              /> */}
              <span className="text-sm text-fg-secondary">
                {command.data.project?.name}
              </span>
            </>
          ) : (
            <span className="text-fg-secondary">No project found</span>
          )}
          <Icon as={CaretRight} className="text-fg-secondary" />
          <Avatar
            src={`https://static1.tokenterminal.com/${command.data.product.logo}`}
            className="w-4 h-4 rounded-full overflow-hidden"
          />
          {command.data.product.product_name}
          {command.data.product.symbol && (
            <span
              className="text-fg-secondary opacity-80"
              style={{
                fontFamily: "var(--font-geist-mono)",
                fontWeight: 400,
              }}
            >
              {command.data.product.symbol}
            </span>
          )}
        </div>
      )
      typeText = "Product"
      linkProps = {
        href: `/projects/${command.data.product.project_slug}/${command.data.product.product_id}`,
      }
      break

    case "marketSector":
      children = (
        <div className="flex items-center gap-2">
          <Icon
            as={
              iconsBySlug[command.data.slug]
                ? iconsBySlug[command.data.slug]
                : Star
            }
          />
          {command.title}
        </div>
      )
      typeText = "Market sector"
      linkProps = { href: `/markets/${command.data.slug}` }
      break

    case "metric":
      children = (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center rounded bg-neutral-950/5 dark:bg-neutral-50/5">
            <Icon as={Database} className="text-fg-secondary" />
          </div>
          {command.data.title}
        </div>
      )
      typeText = "Metric"
      linkProps = {
        href: `/metrics/${command.data.slug}`,
      }
      break

    case "dataset":
      children = (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center rounded bg-neutral-950/5 dark:bg-neutral-50/5">
            <Icon as={command.data.icon} className="text-fg-secondary" />
          </div>
          {command.data.name}
        </div>
      )
      typeText = "Dataset"
      linkProps = {
        href: `/datasets/${command.data.slug}`,
      }
      break

    case "project-metric":
      children = (
        <div className="flex items-center gap-2">
          <Avatar
            src={`https://static1.tokenterminal.com/${command.data.project.logo}`}
            className="w-4 h-4 rounded-full overflow-hidden"
          />
          <div className="flex items-center gap-1">
            <span className="text-sm">{command.data.project.name}</span>
            <Icon as={CaretRight} className="text-fg-secondary" />
            <span className="text-sm">{command.data.metric.title}</span>
          </div>
        </div>
      )
      typeText = "Project metric"
      linkProps = {
        href: command.data.metric.isEcosystemMetric
          ? `/projects/${command.data.project.slug}/ecosystem/${command.data.metric.slug}`
          : `/projects/${command.data.project.slug}/metrics/${command.data.metric.slug}`,
      }
      break

    case "dashboard":
      children = (
        <div className="flex items-center gap-2">
          <DashboardImage
            size={16}
            logoURL={command.data.logo_url}
            imageAlt={command.title}
            className="shrink-0"
          />
          {command.title}
        </div>
      )
      typeText = "Dashboard"
      linkProps = {
        href: `/studio/dashboards/${command.data.user_dashboard_id}`,
      }
      break

    case "ask":
      children = (
        <div className="flex items-center gap-1.5">
          <Icon as={Sparkle} weight="fill" className="text-fg-secondary" />
          <span className="text-fg-secondary">Ask</span>
          <Icon as={CaretRight} className="text-fg-secondary" />
          {command.data.query}
        </div>
      )
      typeText = "Ask"
      linkProps = {
        href: `/ask?query=${encodeURIComponent(command.data.query)}`,
      }
      break

    case "link":
      children = command.renderTitle ? (
        command.renderTitle()
      ) : (
        <div className="flex items-center gap-2">
          {command.data.icon && (
            <Icon as={command.data.icon} className="text-fg-secondary" />
          )}
          {command.title}
          {command.data.labelBadge && <Badge {...command.data.labelBadge} />}
        </div>
      )
      linkProps = {
        href: command.data.href,
        target: command.data.external ? "_blank" : undefined,
      }
      typeText = command.data.external ? (
        <Icon as={ArrowUpRight} className="text-fg-secondary" />
      ) : null
      break
  }

  if (isRecent) {
    typeText = (
      <div className="flex items-center gap-2">
        <IconButton
          label="Remove from recents"
          size="xsmall"
          icon={X}
          variant="ghost"
          className="text-fg-secondary"
          onPress={() => {
            removeRecentSearchCommand(command.id)
          }}
        />
      </div>
    )
  }

  return (
    <SearchableList.Item
      key={command.key}
      id={command.key}
      value={command.title}
      {...linkProps}
      tabIndex={-1}
      onClick={() => {
        handleClose()
        if (command.type === "ask") {
          addRecentSearchCommand(command.id, "ask", command.data.query)
        } else {
          addRecentSearchCommand(command.id)
        }
      }}
    >
      <div className="flex items-center gap-2 w-full justify-between">
        {children}
        <div className="text-sm text-fg-secondary">{typeText}</div>
      </div>
    </SearchableList.Item>
  )
}
